/* ============================================================
   Dathent — Onboarding (AI chat interview)
   Ported from design pack design/pages/onboarding*.jsx → ES module
   with real backend wiring through api.onboarding.aiMessage.

   Flow:
   - First load shows a hard-coded intro bot message + an example
     text input. As soon as the user answers, every subsequent
     assistant message comes from the AI Router via
     /api/onboarding/ai/message — the server extracts brand-profile
     fields, suggests the next step, and tells us when the
     conversation has enough to graduate (canComplete=true).
   - Sticky dock at the bottom hosts the input widget — text by
     default, with a connect-socials widget on the platforms step
     and a final review card when canComplete is true.
   ============================================================ */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Icons as I } from '../components/pack-icons';
import { BrandIcons } from '../components/pack-icons/brands';
import ThemeToggle from '../components/AppShell/ThemeToggle';
import LangSwitch from '../components/AppShell/LangSwitch';
import { api } from '../services/api/client.js';

const INTRO_MESSAGES = [
  "Hi — I'm Dathent. I'll learn your business in a short chat, then build your first content plan.",
  "We won't ask for anything you wouldn't tell a new freelancer. About five minutes, tops.",
  "First — what does your business actually do? One or two sentences is enough.",
];

const INTRO_SUGGESTIONS = [
  'Boutique skincare brand — small batches, sustainable',
  'B2B SaaS for property managers',
  'Wedding photographer — Paris & Côte d\'Azur',
];

const STEP_LABELS = {
  business: 'Business',
  goals: 'Goals',
  audience: 'Audience',
  platforms: 'Channels',
  visual_style: 'Visual',
  visual_references_optional: 'Visual refs',
  boundaries: 'Boundaries',
  review: 'Review',
  website_analysis: 'Website',
};
const STEP_ORDER = ['business', 'goals', 'audience', 'platforms', 'visual_style', 'boundaries', 'review'];

const CONNECTABLE_NETWORKS = [
  { id: 'instagram', name: 'Instagram' },
  { id: 'facebook',  name: 'Facebook' },
  { id: 'linkedin',  name: 'LinkedIn' },
  { id: 'x',         name: 'X' },
  { id: 'tiktok',    name: 'TikTok' },
  { id: 'telegram',  name: 'Telegram' },
  { id: 'threads',   name: 'Threads' },
];

/* ---------------- Helpers ---------------- */
function progressFor(step) {
  if (!step) return 0;
  if (step === 'review') return 100;
  const idx = STEP_ORDER.indexOf(step);
  if (idx < 0) return 10;
  return Math.round(((idx + 1) / STEP_ORDER.length) * 100);
}

function BotAvatar({ size = 28 }) {
  return (
    <span className="ob-avatar" style={{ width: size, height: size }}>
      <img
        src="/assets/dathent-logo.png"
        alt="Dathent"
        width={size}
        height={size}
        style={{ display: 'block', borderRadius: '50%' }}
      />
    </span>
  );
}

function BotMessage({ text, withAvatar = true }) {
  return (
    <div className={`ob-msg ob-msg-bot ${!withAvatar ? 'ob-msg-no-avatar' : ''}`}>
      {withAvatar ? <BotAvatar size={28} /> : <span style={{ width: 28 }} />}
      <div className="ob-bubble ob-bubble-bot" style={{ whiteSpace: 'pre-wrap' }}>{text}</div>
    </div>
  );
}

function UserBubble({ text }) {
  return (
    <div className="ob-msg ob-msg-user">
      <div className="ob-bubble ob-bubble-user">{text}</div>
    </div>
  );
}

function TypingBubble() {
  return (
    <div className="ob-msg ob-msg-bot">
      <BotAvatar size={28} />
      <div className="ob-bubble ob-bubble-bot ob-typing">
        <span className="ob-tdot" /><span className="ob-tdot" /><span className="ob-tdot" />
      </div>
    </div>
  );
}

/* Drip a list of bot strings one-by-one with a typing pause. */
function useBotDrip(messages, onDone) {
  const [shown, setShown] = useState([]);
  const [typing, setTyping] = useState(false);
  const idx = useRef(0);

  useEffect(() => {
    setShown([]);
    idx.current = 0;
    if (!messages || messages.length === 0) {
      setTyping(false);
      onDone?.();
      return;
    }
    let cancelled = false;
    setTyping(true);
    const tick = () => {
      if (cancelled) return;
      const i = idx.current;
      if (i >= messages.length) {
        setTyping(false);
        onDone?.();
        return;
      }
      const delay = Math.min(1400, 350 + messages[i].length * 12);
      setTyping(true);
      setTimeout(() => {
        if (cancelled) return;
        setShown((s) => [...s, messages[i]]);
        idx.current += 1;
        setTyping(false);
        setTimeout(tick, 350);
      }, delay);
    };
    tick();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages]);

  return { shown, typing };
}

/* ---------------- Connect widget (inline at platforms step) ---------------- */
function ConnectInline({ accounts, onConnect, onSkip }) {
  const connected = new Set(accounts.map((a) => String(a.platform || '').toLowerCase()));
  return (
    <div className="ob-connect">
      {CONNECTABLE_NETWORKS.map((n) => {
        const isConnected = connected.has(n.id);
        return (
          <button
            key={n.id}
            type="button"
            className={`ob-net ob-net-${isConnected ? 'connected' : 'idle'}`}
            onClick={() => !isConnected && onConnect(n.id)}
          >
            <span className={`ob-net-ic ob-net-ic-${n.id}`}>
              {BrandIcons[n.id]?.({ size: 18 })}
            </span>
            <div className="ob-net-text">
              <span className="ob-net-name">{n.name}</span>
              <span className="ob-net-sub">{isConnected ? 'Connected' : 'Click to connect'}</span>
            </div>
            {isConnected
              ? <span className="ob-net-badge"><I.check size={11} /></span>
              : <I.arrowUpR size={13} className="ob-net-arr" />}
          </button>
        );
      })}
      <div className="ob-in-foot" style={{ gridColumn: '1 / -1' }}>
        <span className="ob-in-meta">
          {connected.size > 0 ? `${connected.size} connected` : 'You can also add accounts later in Settings'}
        </span>
        <button type="button" className="btn btn-primary btn-sm" onClick={onSkip}>
          {connected.size > 0 ? 'Continue' : 'Skip for now'} <I.arrowRight size={12} />
        </button>
      </div>
    </div>
  );
}

/* ---------------- Text input dock ---------------- */
function TextDock({ placeholder, suggestions, disabled, onSubmit, hint }) {
  const [value, setValue] = useState('');
  const ref = useRef(null);
  useEffect(() => { ref.current?.focus(); }, []);

  const send = () => {
    const text = value.trim();
    if (!text || disabled) return;
    setValue('');
    onSubmit(text);
  };

  return (
    <div className="ob-in ob-in-text">
      {hint && <div className="ob-in-hint">{hint}</div>}
      <div className="ob-textbox">
        <textarea
          ref={ref}
          rows={2}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
          placeholder={placeholder || 'Type your answer…'}
          disabled={disabled}
        />
        <div className="ob-textbox-foot">
          <div />
          <button
            type="button"
            className="btn btn-primary btn-sm"
            disabled={!value.trim() || disabled}
            onClick={send}
          >
            Send <I.send size={12} />
          </button>
        </div>
      </div>
      {suggestions && suggestions.length > 0 && (
        <div className="ob-sugg">
          <span className="ob-sugg-l">Examples</span>
          {suggestions.map((s) => (
            <button
              key={s}
              type="button"
              className="ob-sugg-pill"
              onClick={() => setValue(s)}
              disabled={disabled}
            >
              {s}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/* ---------------- Review CTA ---------------- */
function ReviewDock({ extracted, onFinalize, finalizing }) {
  const summary = [
    extracted.businessDescription && `Business: ${extracted.businessDescription}`,
    extracted.targetAudience && `Audience: ${extracted.targetAudience}`,
    Array.isArray(extracted.goals) && extracted.goals.length && `Goal: ${extracted.goals.join(', ')}`,
    Array.isArray(extracted.preferredPlatforms) && extracted.preferredPlatforms.length
      && `Channels: ${extracted.preferredPlatforms.join(' · ')}`,
    extracted.visualStyle && `Visual: ${extracted.visualStyle}`,
  ].filter(Boolean);

  return (
    <div className="ob-in">
      <div className="card" style={{ padding: 18, display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div className="eyebrow"><span>Ready to finalize</span></div>
        <h3 style={{ margin: 0, fontSize: 18 }}>Your AI setup is ready</h3>
        {summary.length > 0 && (
          <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 6 }}>
            {summary.map((line, i) => (
              <li key={i} style={{ fontSize: 13.5, color: 'var(--text-muted)' }}>{line}</li>
            ))}
          </ul>
        )}
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <button
            type="button"
            className="btn btn-primary"
            disabled={finalizing}
            onClick={onFinalize}
          >
            {finalizing ? 'Building your workspace…' : 'Build my workspace'} <I.arrowRight size={12} />
          </button>
        </div>
      </div>
    </div>
  );
}

/* ---------------- Top-level Onboarding ---------------- */
export const Onboarding = ({ onComplete }) => {
  const navigate = useNavigate();
  // history: [{ kind:'bot', text } | { kind:'user', text } | { kind:'connect' }]
  const [history, setHistory] = useState([]);
  const [currentStep, setCurrentStep] = useState('business');
  const [extracted, setExtracted] = useState({});
  const [uiHints, setUiHints] = useState({});
  const [canComplete, setCanComplete] = useState(false);
  const [pending, setPending] = useState([]);          // bot messages waiting to drip
  const [pendingTyping, setPendingTyping] = useState(true);
  const [sending, setSending] = useState(false);
  const [finalizing, setFinalizing] = useState(false);
  const [accounts, setAccounts] = useState([]);
  const [error, setError] = useState(null);
  const scrollRef = useRef(null);
  const lastFinishedAtRef = useRef(0);

  /* On mount — drip the intro, fetch connected accounts. */
  useEffect(() => {
    setPending(INTRO_MESSAGES);
    let cancelled = false;
    api.socialAccounts.list()
      .then((res) => {
        if (cancelled) return;
        const list = res?.accounts || res?.data?.accounts || res?.data || [];
        setAccounts(Array.isArray(list) ? list : []);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const { shown: pendingShown, typing: pendingDripTyping } = useBotDrip(pending, () => {
    setPendingTyping(false);
    // Flush dripped messages into history
    setHistory((h) => [
      ...h,
      ...pending.map((text) => ({ kind: 'bot', text })),
    ]);
    setPending([]);
    lastFinishedAtRef.current = Date.now();
  });

  /* Auto-scroll on any update */
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    requestAnimationFrame(() => {
      el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
    });
  }, [history, pendingShown, pendingDripTyping, sending]);

  const sendAnswer = async (text) => {
    if (sending) return;
    setError(null);
    setSending(true);
    setHistory((h) => [...h, { kind: 'user', text }]);
    try {
      const res = await api.onboarding.aiMessage(text, currentStep);
      const data = res?.data || res || {};
      const assistantMessage = data.assistantMessage || data.message || '';
      const nextStep = data.suggestedNextStep || currentStep;
      const nextExtracted = data.extracted || {};
      const nextHints = data.uiHints || {};
      const completable = Boolean(data.canComplete);

      setExtracted((prev) => ({ ...prev, ...nextExtracted }));
      setCurrentStep(nextStep);
      setUiHints(nextHints);
      setCanComplete(completable);

      // If the assistant suggested platforms step, surface the connect widget once
      const showSocial = Boolean(nextHints.showSocialConnect) || nextStep === 'platforms';
      const alreadyOfferedConnect = history.some((h) => h.kind === 'connect');
      if (showSocial && !alreadyOfferedConnect) {
        // Bot message then connect card
        setPending(assistantMessage ? [assistantMessage] : []);
        setPendingTyping(true);
        // Append connect card after drip completes by enqueueing on next drip-done effect.
        // Simpler approach: append connect right after pending settles.
        setTimeout(() => {
          setHistory((h) => h.some((x) => x.kind === 'connect') ? h : [...h, { kind: 'connect' }]);
        }, 50 + (assistantMessage?.length ?? 0) * 12);
      } else if (assistantMessage) {
        setPending([assistantMessage]);
        setPendingTyping(true);
      }
    } catch (err) {
      setError(err?.message || 'Unable to continue. Please retry.');
    } finally {
      setSending(false);
    }
  };

  const handleConnect = async (platform) => {
    try {
      const result = await api.socialAccounts.connect(platform, {
        returnPath: '/onboarding',
      });
      if (result?.link?.url || result?.deepLink) {
        const url = result.link?.url || result.deepLink;
        window.open(url, '_blank', 'noopener');
      }
      // Refresh accounts after small delay (OAuth happens out-of-band)
      setTimeout(async () => {
        const fresh = await api.socialAccounts.list().catch(() => null);
        if (fresh) {
          const list = fresh?.accounts || fresh?.data?.accounts || fresh?.data || [];
          setAccounts(Array.isArray(list) ? list : []);
        }
      }, 800);
    } catch (err) {
      setError(err?.message || 'Could not start connect');
    }
  };

  const skipConnect = () => {
    // Send a synthetic answer so the AI advances past platforms.
    const connected = accounts.map((a) => String(a.platform || '').toLowerCase()).filter(Boolean);
    const answer = connected.length
      ? `Connected: ${connected.join(', ')}`
      : 'Skip for now — I will connect accounts later from Settings.';
    sendAnswer(answer);
  };

  const handleFinalize = async () => {
    if (finalizing) return;
    setFinalizing(true);
    try {
      // Persist whatever we extracted to brand_profile, then mark session done.
      const brandPayload = {
        businessDescription: extracted.businessDescription || extracted.businessName || 'Business description pending',
        targetAudience: extracted.targetAudience || 'Audience pending',
        offer: extracted.offer || null,
        goals: Array.isArray(extracted.goals) ? extracted.goals : (extracted.goals ? [extracted.goals] : ['visibility']),
        positioning: extracted.positioning || null,
        toneOfVoice: extracted.toneOfVoice || 'professional',
        contentPillars: Array.isArray(extracted.contentPillars) ? extracted.contentPillars : [],
        visualStyle: extracted.visualStyle || 'natural',
        preferredPlatforms: Array.isArray(extracted.preferredPlatforms) ? extracted.preferredPlatforms : [],
        language: 'en',
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
      };
      await api.profile.updateBrandProfile(brandPayload).catch(() => null);
      await api.onboarding.complete().catch(() => null);
      onComplete?.('dashboard');
      navigate('/app', { replace: true });
    } catch (err) {
      setError(err?.message || 'Could not finalize');
    } finally {
      setFinalizing(false);
    }
  };

  /* What renders in the sticky dock at the bottom */
  const renderDock = () => {
    if (canComplete) {
      return <ReviewDock extracted={extracted} onFinalize={handleFinalize} finalizing={finalizing} />;
    }
    // First answer uses the intro suggestions; subsequent answers no suggestions.
    const isFirst = history.filter((h) => h.kind === 'user').length === 0;
    return (
      <TextDock
        placeholder={
          currentStep === 'business' ? 'e.g. We design and ship handmade ceramics for cafés in Berlin.' :
          currentStep === 'audience' ? 'e.g. Café owners 30–50 in Berlin who value provenance.' :
          currentStep === 'goals'    ? 'e.g. Drive 30% more wholesale inquiries this quarter.' :
          currentStep === 'visual_style' ? 'e.g. Natural and textural, soft daylight, hand-shot.' :
          currentStep === 'boundaries' ? 'e.g. Never discuss competitor names; avoid AI-stylised faces.' :
          'Type your answer…'
        }
        suggestions={isFirst ? INTRO_SUGGESTIONS : null}
        disabled={sending || pendingTyping || pending.length > 0}
        onSubmit={sendAnswer}
      />
    );
  };

  const progress = progressFor(currentStep);
  const stepLabel = STEP_LABELS[currentStep] || 'Setup';

  return (
    <div className="ob-root">
      <header className="ob-top">
        <div className="ob-brand">
          <img src="/assets/dathent-logo.png" alt="" width="28" height="28" style={{ borderRadius: 6, display: 'block' }} />
          <span>Dathent</span>
          <span className="ob-brand-sub mono">onboarding</span>
        </div>

        <div className="ob-progress">
          <div className="ob-progress-meta">
            <span className="mono">{stepLabel}</span>
            <span className="mono">{progress}%</span>
          </div>
          <div className="ob-progress-bar">
            <div className="ob-progress-fill" style={{ width: `${progress}%` }} />
          </div>
        </div>

        <div className="ob-top-r">
          <ThemeToggle />
          <LangSwitch />
        </div>
      </header>

      <div className="ob-stage" ref={scrollRef}>
        <div className="ob-bg-glow" />
        <div className="ob-thread">
          {/* Historic messages */}
          {history.map((entry, i) => {
            if (entry.kind === 'bot') {
              const prev = history[i - 1];
              return <BotMessage key={i} text={entry.text} withAvatar={!prev || prev.kind !== 'bot'} />;
            }
            if (entry.kind === 'user') {
              return <UserBubble key={i} text={entry.text} />;
            }
            if (entry.kind === 'connect') {
              return (
                <div key={i} className="ob-msg ob-msg-bot ob-msg-no-avatar" style={{ marginTop: 8 }}>
                  <span style={{ width: 28 }} />
                  <div style={{ flex: 1 }}>
                    <ConnectInline
                      accounts={accounts}
                      onConnect={handleConnect}
                      onSkip={skipConnect}
                    />
                  </div>
                </div>
              );
            }
            return null;
          })}

          {/* Currently-dripping bot messages */}
          {pendingShown.map((text, i) => (
            <BotMessage key={`drip-${i}`} text={text} withAvatar={i === 0 && history[history.length - 1]?.kind !== 'bot'} />
          ))}
          {(pendingDripTyping || (sending && pending.length === 0)) && <TypingBubble />}

          {error && (
            <div className="ob-msg ob-msg-bot ob-msg-no-avatar">
              <span style={{ width: 28 }} />
              <div className="ob-bubble ob-bubble-bot" style={{ color: 'var(--danger-text, var(--danger))' }}>
                {error}
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="ob-dock">
        <div className="ob-dock-inner">{renderDock()}</div>
      </div>
    </div>
  );
};

export default Onboarding;
